import { PlusIcon, Trash01Icon } from '@langchain/untitled-ui-icons';

import { useState } from 'react';

import { useHostCreateListenerMutation } from '@/Pages/HostListeners/hooks/useHostCreateListenerMutation';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Pane } from '@/components/Pane';
import { Button, IconButton, Input, Text } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions';

interface HostListenerCrudPaneProps {
  isOpen: boolean;
  doClose: () => void;
}

const COMPUTE_ID_REQUIRED_ERROR = 'Compute ID is required';
const COMPUTE_ID_WHITESPACE_ERROR =
  'Compute ID must not contain whitespace characters';
const COMPUTE_ID_LENGTH_ERROR =
  'Compute ID must not be longer than 64 characters';
const K8S_NAMESPACES_REQUIRED_ERROR = 'At least one namespace is required';

const validateComputeId = (value: string): string => {
  if (!value) {
    return COMPUTE_ID_REQUIRED_ERROR;
  }
  if (value.length > 64) {
    return COMPUTE_ID_LENGTH_ERROR;
  }
  if (/\s/.test(value)) {
    return COMPUTE_ID_WHITESPACE_ERROR;
  }
  return '';
};

export function HostListenerCrudPane({
  isOpen,
  doClose,
}: HostListenerCrudPaneProps) {
  const { authorize } = usePermissions();
  const [computeId, setComputeId] = useState<string>('');
  const [kubernetesNamespaces, setKubernetesNamespaces] = useState<string[]>([
    '',
  ]);
  const [computeIdHelperText, setComputeIdHelperText] = useState<string>('');
  const [namespacesHelperText, setNamespacesHelperText] = useState<string>('');

  const create = useHostCreateListenerMutation();

  const handleComputeIdChange = (value: string) => {
    setComputeId(value);
    setComputeIdHelperText('');

    const errorMessage = validateComputeId(value);
    if (errorMessage) {
      setComputeIdHelperText(errorMessage);
    }
  };

  const handleNamespaceChange = (index: number, value: string) => {
    const newNamespaces = [...kubernetesNamespaces];
    newNamespaces[index] = value;
    setKubernetesNamespaces(newNamespaces);
    setNamespacesHelperText('');

    if (!newNamespaces.some((ns) => ns.trim().length > 0)) {
      setNamespacesHelperText(K8S_NAMESPACES_REQUIRED_ERROR);
    }
  };

  const addNamespace = () => {
    setKubernetesNamespaces([...kubernetesNamespaces, '']);
  };

  const removeNamespace = (index: number) => {
    if (kubernetesNamespaces.length > 1) {
      const newNamespaces = kubernetesNamespaces.filter((_, i) => i !== index);
      setKubernetesNamespaces(newNamespaces);
    }
  };

  const handleSubmit = async () => {
    const errorMessage = validateComputeId(computeId);
    if (errorMessage) {
      setComputeIdHelperText(errorMessage);
      return;
    }

    const namespacesList = kubernetesNamespaces
      .map((ns) => ns.trim())
      .filter((ns) => ns.length > 0);

    if (namespacesList.length === 0) {
      setNamespacesHelperText(K8S_NAMESPACES_REQUIRED_ERROR);
      return;
    }

    const listener = await create.trigger({
      compute_type: 'k8s',
      compute_id: computeId,
      compute_config: {
        k8s_namespaces: namespacesList,
      },
    });

    if (listener) {
      doClose();
    }
  };

  const cancelAndSubmitButtons = (
    <div className="mr-4 flex items-center gap-2">
      <div>
        <Button
          type="button"
          onClick={doClose}
          color="secondary"
          variant="outlined"
          size="md"
        >
          Cancel
        </Button>
      </div>
      <div>
        <Button
          type="submit"
          color="primary"
          loading={create.isMutating}
          onClick={handleSubmit}
          size="md"
          disabled={!!(computeIdHelperText || namespacesHelperText)}
        >
          Submit
        </Button>
      </div>
    </div>
  );

  if (!authorize('deployments:create')) {
    return null;
  }

  return (
    <Pane
      open={isOpen}
      onClose={doClose}
      title="Create listener"
      topBarRightElement={cancelAndSubmitButtons}
    >
      <div className="flex flex-col gap-1">
        <Text variant="h1" weight="semibold">
          Create New Listener
        </Text>
        <Text variant="body" className="text-tertiary">
          Create a listener to connect your Kubernetes cluster to LangGraph
          Platform
        </Text>
      </div>

      <div className="h-4" />

      {create.error && <ExpandableErrorAlert error={create.error} />}

      <form
        className="flex flex-col gap-8"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
      >
        <div className="grid grid-cols-[minmax(120px,1fr),minmax(auto,800px),1fr] gap-x-8 gap-y-5">
          <hr className="col-start-1 col-end-4 border-tertiary" />

          <div className="flex flex-col items-start">
            <Text variant="h3">Listener setup</Text>
            <Text variant="body" className="text-tertiary">
              Specify a unique identifier and add Kubernetes namespaces for this
              listener. See the{' '}
              <a
                href="https://docs.langchain.com/langgraph-platform/hybrid"
                className="underline"
                target="_blank"
              >
                docs
              </a>{' '}
              for more information.
            </Text>
          </div>
          <div className="flex flex-col gap-4">
            <Input
              label="Compute ID"
              hintText={computeIdHelperText}
              onChange={handleComputeIdChange}
              placeholder="e.g. k8s-cluster-dev-01"
              value={computeId}
              required
              isError={computeIdHelperText !== ''}
            />
            {kubernetesNamespaces.map((namespace, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  className="w-fit"
                  hintText={index === 0 ? namespacesHelperText : ''}
                  label={index === 0 && 'Kubernetes namespace(s)'}
                  placeholder="Enter namespace"
                  value={namespace}
                  onChange={(val) => handleNamespaceChange(index, val)}
                  required={index === 0}
                  isError={index === 0 && namespacesHelperText !== ''}
                />
                {kubernetesNamespaces.length > 1 && index !== 0 && (
                  <IconButton
                    icon={Trash01Icon}
                    label="Remove namespace"
                    color="secondary"
                    variant="outlined"
                    onClick={() => removeNamespace(index)}
                    size="md"
                  />
                )}
              </div>
            ))}
            <Button
              size="sm"
              className="w-fit"
              variant="outlined"
              leftDecorator={PlusIcon}
              onClick={addNamespace}
              type="button"
              color="secondary"
            >
              Add Namespace
            </Button>
          </div>
        </div>
      </form>
    </Pane>
  );
}
